import { Badge, Button, EmptyState, buttonVariants } from "@coucou/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { loadSession } from "../lib/session";
import { API_URL, SALON_ID, api } from "../lib/trpc";

/**
 * Espace documentaire.
 *
 * Deux vues, une seule source : `MesDocuments` pour ce qu'une employee doit
 * pouvoir retrouver seule, `DocumentsPanel` pour ce que la gerante depose.
 */

export const NATURES = [
  { value: "convention_collective", label: "Convention collective", equipe: true },
  { value: "reglement_interieur", label: "Règlement intérieur", equipe: true },
  { value: "note_interne", label: "Note interne", equipe: true },
  { value: "contrat", label: "Contrat", equipe: false },
  { value: "avenant", label: "Avenant", equipe: false },
  { value: "fiche_de_paie", label: "Fiche de paie", equipe: false },
  { value: "attestation", label: "Attestation", equipe: false },
  { value: "autre", label: "Autre", equipe: true },
] as const;

/**
 * Natures posees par l'API seule, absentes du formulaire de depot : un
 * planning se depose depuis Reglages > Horaires, la ou il se construit.
 */
const NATURES_AUTOMATIQUES: Record<string, string> = { planning: "Planning" };

const libelleNature = (valeur: string) =>
  NATURES.find((nature) => nature.value === valeur)?.label ??
  NATURES_AUTOMATIQUES[valeur] ??
  valeur;

const poids = (octets: number) =>
  octets < 1024 * 1024
    ? `${Math.round(octets / 1024)} Ko`
    : `${(octets / 1024 / 1024).toFixed(1)} Mo`;

/**
 * Telechargement d'un document protege.
 *
 * Une balise `<a href>` ne porte pas l'en-tete `Authorization`, et le poste du
 * salon n'a pas de cookie de session : un lien direct recevrait un 401. On va
 * donc chercher le fichier en JavaScript, avec le jeton, puis on declenche
 * l'enregistrement depuis la memoire.
 */
async function telecharger(id: string, nomFichier: string): Promise<void> {
  const session = loadSession();
  const reponse = await fetch(`${API_URL}/documents/${id}`, {
    headers: session ? { authorization: `Bearer ${session.token}` } : {},
  });

  if (!reponse.ok) {
    throw new Error(
      reponse.status === 404
        ? "Ce document n'est plus disponible."
        : `Téléchargement impossible (${reponse.status}).`,
    );
  }

  const blob = await reponse.blob();
  const url = URL.createObjectURL(blob);
  const lien = document.createElement("a");
  lien.href = url;
  lien.download = nomFichier;
  lien.click();
  // Sans revocation, le blob reste en memoire jusqu'au rechargement de la page.
  URL.revokeObjectURL(url);
}

function BoutonTelecharger({ id, nom }: { id: string; nom: string }) {
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        loading={enCours}
        onClick={async () => {
          setErreur(null);
          setEnCours(true);
          try {
            await telecharger(id, nom);
          } catch (error) {
            setErreur(error instanceof Error ? error.message : "Échec.");
          } finally {
            setEnCours(false);
          }
        }}
      >
        Télécharger
      </Button>
      {erreur && (
        <span role="alert" className="ml-2 text-sm text-grenat-700">
          {erreur}
        </span>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Vue employee                                                               */
/* -------------------------------------------------------------------------- */

export function MesDocuments() {
  const documents = useQuery({
    queryKey: ["mes-documents"],
    queryFn: () => api.admin.documents.mine.query(),
  });

  const equipe = (documents.data ?? []).filter((doc) => doc.shared);
  const personnels = (documents.data ?? []).filter((doc) => !doc.shared);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="shrink-0 px-6 pb-2 pt-4">
        <h1 className="text-2xl">Mes documents</h1>
        <p className="mt-1 text-sm text-encre-500">
          Vos documents personnels et ceux qui s'appliquent à toute l'équipe.
        </p>
      </header>

      <div className="min-h-0 flex-1 space-y-8 overflow-auto px-6 pb-8 pt-4">
        {documents.isPending && <div className="h-24 animate-pulse rounded-xl bg-creme-200" />}

        {documents.data?.length === 0 && (
          <EmptyState
            variant="card"
            glyph="📄"
            title="Aucun document pour l'instant"
            description="Les règlements et vos documents personnels apparaîtront ici dès qu'ils seront déposés."
          />
        )}

        <Section titre="Mes documents personnels" documents={personnels} />
        <Section titre="Pour toute l'équipe" documents={equipe} />
      </div>
    </div>
  );
}

function Section({
  titre,
  documents,
}: {
  titre: string;
  documents: {
    id: string;
    kind: string;
    title: string;
    originalName: string;
    sizeBytes: number;
    periodLabel: string | null;
    createdAt: Date;
  }[];
}) {
  if (documents.length === 0) return null;

  return (
    <section>
      <h2 className="mb-3 text-lg">{titre}</h2>
      <ul className="divide-y divide-creme-200 overflow-hidden rounded-xl border border-creme-200 bg-creme-50">
        {documents.map((doc) => (
          <li key={doc.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-encre-900">{doc.title}</p>
              <p className="mt-0.5 text-sm text-encre-400">
                {libelleNature(doc.kind)}
                {doc.periodLabel ? ` · ${doc.periodLabel}` : ""} · {poids(doc.sizeBytes)} ·{" "}
                {new Date(doc.createdAt).toLocaleDateString("fr-FR")}
              </p>
            </div>
            <BoutonTelecharger id={doc.id} nom={doc.originalName} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Vue gerante                                                                */
/* -------------------------------------------------------------------------- */

export function DocumentsPanel() {
  const queryClient = useQueryClient();
  const [filtre, setFiltre] = useState<string>("");

  const equipe = useQuery({
    queryKey: ["team"],
    queryFn: () => api.admin.team.list.query({ salonId: SALON_ID }),
  });

  const documents = useQuery({
    queryKey: ["documents", filtre],
    queryFn: () =>
      api.admin.documents.all.query({ salonId: SALON_ID, userId: filtre || undefined }),
  });

  const retirer = useMutation({
    mutationFn: (id: string) => api.admin.documents.remove.mutate({ id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["documents"] }),
  });

  return (
    <div className="space-y-6">
      <DepotDocument
        membres={equipe.data ?? []}
        onDepose={() => {
          queryClient.invalidateQueries({ queryKey: ["documents"] });
          queryClient.invalidateQueries({ queryKey: ["mes-documents"] });
        }}
      />

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-encre-600" htmlFor="filtre-employee">
          Afficher
        </label>
        <select
          id="filtre-employee"
          value={filtre}
          onChange={(event) => setFiltre(event.target.value)}
          className="h-9 rounded-lg border border-creme-300 bg-white px-3 text-sm"
        >
          <option value="">Tous les documents</option>
          {(equipe.data ?? []).map((membre) => (
            <option key={membre.id} value={membre.id}>
              {membre.firstName}
            </option>
          ))}
        </select>
      </div>

      {documents.data?.length === 0 && (
        <EmptyState
          variant="card"
          glyph="📄"
          title="Aucun document"
          description="Déposez la convention collective, le règlement intérieur, les contrats et les fiches de paie."
        />
      )}

      {(documents.data?.length ?? 0) > 0 && (
        <ul className="divide-y divide-creme-200 overflow-hidden rounded-xl border border-creme-200 bg-creme-50">
          {documents.data!.map((doc) => (
            <li key={doc.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-encre-900">{doc.title}</p>
                <p className="mt-0.5 text-sm text-encre-400">
                  {libelleNature(doc.kind)}
                  {doc.periodLabel ? ` · ${doc.periodLabel}` : ""} · {poids(doc.sizeBytes)} ·{" "}
                  {new Date(doc.createdAt).toLocaleDateString("fr-FR")}
                </p>
              </div>
              {doc.userId ? (
                <Badge tone="accent">{doc.ownerFirstName ?? "Personnel"}</Badge>
              ) : (
                <Badge>Toute l'équipe</Badge>
              )}
              <BoutonTelecharger id={doc.id} nom={doc.originalName} />
              <Button
                size="sm"
                variant="ghost"
                loading={retirer.isPending && retirer.variables === doc.id}
                onClick={() => retirer.mutate(doc.id)}
              >
                Retirer
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function DepotDocument({
  membres,
  onDepose,
}: {
  membres: { id: string; firstName: string }[];
  onDepose: () => void;
}) {
  const [titre, setTitre] = useState("");
  const [nature, setNature] = useState<string>("note_interne");
  const [destinataire, setDestinataire] = useState("");
  const [periode, setPeriode] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  /*
   * Une nature personnelle sans destinataire n'a pas de sens : une fiche de
   * paie « pour toute l'equipe » serait une fuite. On l'empeche a la saisie
   * plutot que de la rattraper apres.
   */
  const naturePersonnelle = !NATURES.find((n) => n.value === nature)?.equipe;
  const incomplet = !titre.trim() || (naturePersonnelle && !destinataire);

  const envoyer = async (fichier: File) => {
    setErreur(null);
    setEnCours(true);
    try {
      const session = loadSession();
      const formulaire = new FormData();
      formulaire.append("fichier", fichier);
      formulaire.append("salonId", SALON_ID);
      formulaire.append("kind", nature);
      formulaire.append("title", titre.trim());
      if (destinataire) formulaire.append("userId", destinataire);
      if (periode.trim()) formulaire.append("periodLabel", periode.trim());

      const reponse = await fetch(`${API_URL}/documents`, {
        method: "POST",
        headers: session ? { authorization: `Bearer ${session.token}` } : {},
        body: formulaire,
      });

      if (!reponse.ok) {
        throw new Error(
          reponse.status === 415
            ? "Format refusé. Acceptés : PDF, JPEG, PNG, Word."
            : reponse.status === 403
              ? "Seule une gérante peut déposer un document."
              : `Le serveur a refusé l'envoi (${reponse.status}).`,
        );
      }

      setTitre("");
      setPeriode("");
      onDepose();
    } catch (error) {
      setErreur(error instanceof Error ? error.message : "Envoi impossible.");
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="rounded-xl border border-creme-200 bg-creme-50 p-5">
      <h2 className="mb-4 text-lg">Déposer un document</h2>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-sm text-encre-600">Intitulé</span>
          <input
            value={titre}
            onChange={(event) => setTitre(event.target.value)}
            placeholder="Fiche de paie"
            className="h-10 w-full rounded-lg border border-creme-300 bg-white px-3 text-sm"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm text-encre-600">Nature</span>
          <select
            value={nature}
            onChange={(event) => {
              setNature(event.target.value);
              // Une nature d'equipe efface le destinataire : le garder ferait
              // un « reglement interieur » adresse a une seule personne.
              if (NATURES.find((n) => n.value === event.target.value)?.equipe) {
                setDestinataire("");
              }
            }}
            className="h-10 w-full rounded-lg border border-creme-300 bg-white px-3 text-sm"
          >
            {NATURES.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm text-encre-600">
            Destinataire{naturePersonnelle ? "" : " (facultatif)"}
          </span>
          <select
            value={destinataire}
            onChange={(event) => setDestinataire(event.target.value)}
            className="h-10 w-full rounded-lg border border-creme-300 bg-white px-3 text-sm"
          >
            <option value="">Toute l'équipe</option>
            {membres.map((membre) => (
              <option key={membre.id} value={membre.id}>
                {membre.firstName}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm text-encre-600">Période (facultatif)</span>
          <input
            value={periode}
            onChange={(event) => setPeriode(event.target.value)}
            placeholder="Août 2026"
            className="h-10 w-full rounded-lg border border-creme-300 bg-white px-3 text-sm"
          />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label className={incomplet || enCours ? "pointer-events-none opacity-45" : ""}>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.docx"
            className="sr-only"
            disabled={incomplet || enCours}
            onChange={(event) => {
              const fichier = event.target.files?.[0];
              // On vide le champ : sans cela, deposer deux fois le meme fichier
              // ne declenche aucun evenement.
              event.target.value = "";
              if (fichier) void envoyer(fichier);
            }}
          />
          {/* Un `span` et non un `Button` : un vrai bouton n'active pas le
              `label` qui l'entoure, et le selecteur de fichier ne s'ouvrirait
              jamais. */}
          <span className={buttonVariants({ variant: "primary", size: "md" })}>
            {enCours ? "Envoi…" : "Choisir un fichier"}
          </span>
        </label>

        {naturePersonnelle && !destinataire && (
          <span className="text-sm text-ambre-700">
            Cette nature est personnelle : choisissez une destinataire.
          </span>
        )}
      </div>

      {erreur && (
        <p role="alert" className="mt-3 text-sm text-grenat-700">
          {erreur}
        </p>
      )}
    </div>
  );
}
